import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { TILE_ATTRIBUTION, TILE_URL, loadLeaflet } from '@cnt-workspace/ui';

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795]; // center of US
const DEFAULT_ZOOM = 4;
const PINNED_ZOOM = 13;

/**
 * Small click-to-place-pin map for the activity-location add/edit form.
 * Own copy per the convention already used 6+ times across feature-cnt
 * (every lib that needs a map builds its own small inline Leaflet
 * component via `await import('leaflet')` for SSR safety) rather than
 * extracting a new shared component — see `Phase1AddressComponent` in
 * `hosting-new-listing` for the pattern this mirrors.
 */
@Component({
  selector: 'cnt-activity-location-map',
  standalone: true,
  // Leaflet fills the map div with its own pane/tile tree, which Angular did
  // not render and must not try to match against the server output.
  host: { ngSkipHydration: 'true' },
  imports: [CommonModule],
  // Height is set inline (not via a Tailwind h-* utility) because Leaflet needs
  // the container to have a real, already-applied size at init time — relying
  // on a utility class the JIT scanner may not have generated yet (e.g. one
  // that appears nowhere else in the app, so nothing else forces it into the
  // build) silently leaves the container at 0 height and the map invisible.
  template: `<div #mapEl class="w-full rounded-xl overflow-hidden" style="height: 256px;"></div>`,
})
export class ActivityLocationMapComponent implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);

  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;

  @Input() lat?: number;
  @Input() lng?: number;
  @Output() positionChange = new EventEmitter<{ lat: number; lng: number }>();

  private map: import('leaflet').Map | null = null;
  private marker: import('leaflet').Marker | null = null;
  /** See ngAfterViewInit — guards against building a map after teardown. */
  private destroyed = false;

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    const L = await loadLeaflet();
    // See phase1-address: the component can be destroyed while this await is
    // pending, and the other map components all re-check here.
    if (this.destroyed) return;
    const center: [number, number] =
      typeof this.lat === 'number' && typeof this.lng === 'number' ? [this.lat, this.lng] : DEFAULT_CENTER;
    const zoom = typeof this.lat === 'number' ? PINNED_ZOOM : DEFAULT_ZOOM;

    this.map = L.map(this.mapEl.nativeElement, {
      center,
      zoom,
      zoomControl: true,
      scrollWheelZoom: false,
      preferCanvas: true,
    });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 18 }).addTo(this.map);

    if (typeof this.lat === 'number' && typeof this.lng === 'number') {
      this.placeMarker(L, [this.lat, this.lng]);
    }

    this.map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      const rounded = { lat: +lat.toFixed(6), lng: +lng.toFixed(6) };
      this.placeMarker(L, [rounded.lat, rounded.lng]);
      this.positionChange.emit(rounded);
    });
  }

  /** Re-syncs the pin when the parent's lat/lng change externally (e.g. manual
   *  number-input edits or "use my location"). */
  async setPosition(lat: number, lng: number): Promise<void> {
    if (!this.map) return;
    const L = await loadLeaflet();
    this.map.setView([lat, lng], Math.max(this.map.getZoom(), PINNED_ZOOM));
    this.placeMarker(L, [lat, lng]);
  }

  private placeMarker(L: typeof import('leaflet'), center: [number, number]): void {
    if (!this.map) return;
    if (this.marker) {
      this.marker.setLatLng(center);
      return;
    }
    const icon = L.divIcon({
      html: `<div style="width:20px;height:20px;border-radius:50%;background:#e3530d;border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,0.3);"></div>`,
      className: 'cnt-activity-pin',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    this.marker = L.marker(center, { icon, draggable: true }).addTo(this.map);
    this.marker.on('dragend', () => {
      const ll = this.marker!.getLatLng();
      const rounded = { lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) };
      this.positionChange.emit(rounded);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.map?.remove();
    this.map = null;
    this.marker = null;
  }
}
